<template>
    <v-menu
        v-model="menuOpen"
        :close-on-content-click="false"
        location="bottom start"
    >
        <template #activator="{ props: menuProps }">
            <v-text-field
                v-bind="menuProps"
                :model-value="displayValue"
                :label="label"
                variant="outlined"
                density="compact"
                hide-details="auto"
                readonly
                :clearable="hasSelection"
                append-inner-icon="mdi-chevron-down"
                @click:clear.stop="clearSelection"
            />
        </template>
        <v-card min-width="320" elevation="8">
            <v-card-text class="d-flex flex-column ga-3 pt-4">
                <v-autocomplete
                    :model-value="parent"
                    :items="parentItems"
                    :item-title="itemTitle"
                    :item-value="itemValue"
                    :label="parentLabel"
                    :multiple="multiple"
                    :chips="multiple"
                    :closable-chips="multiple"
                    :custom-filter="diacriticInsensitiveFilter"
                    variant="outlined"
                    density="compact"
                    hide-details
                    clearable
                    auto-select-first
                    @update:model-value="onParentChange"
                />
                <v-autocomplete
                    :model-value="child"
                    :items="childItems"
                    :item-title="itemTitle"
                    :item-value="itemValue"
                    :label="childLabel"
                    :custom-filter="diacriticInsensitiveFilter"
                    variant="outlined"
                    density="compact"
                    hide-details
                    clearable
                    auto-select-first
                    :disabled="!hasParentSelection"
                    :loading="childLoading"
                    @update:model-value="onChildChange"
                />
            </v-card-text>
        </v-card>
    </v-menu>
</template>

<script>
import { diacriticInsensitiveFilter } from '@/utils/textSearch';

function asParentArray(value, multiple) {
    if (multiple) {
        return Array.isArray(value) ? value.filter((entry) => entry != null && entry !== '') : [];
    }
    if (value == null || value === '') {
        return [];
    }
    return [value];
}

export default {
    name: 'HierarchicalFilterSelect',
    props: {
        parent: {
            type: [Array, String, Number, null],
            default: null,
        },
        child: {
            type: [String, Number, null],
            default: null,
        },
        label: {
            type: String,
            required: true,
        },
        parentLabel: {
            type: String,
            required: true,
        },
        childLabel: {
            type: String,
            required: true,
        },
        parentItems: {
            type: Array,
            default: () => [],
        },
        childItems: {
            type: Array,
            default: () => [],
        },
        itemTitle: {
            type: String,
            default: 'title',
        },
        itemValue: {
            type: String,
            default: 'value',
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        childLoading: {
            type: Boolean,
            default: false,
        },
        closeOnChildSelect: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['update:parent', 'update:child', 'parent-change', 'child-change', 'clear'],
    data() {
        return {
            menuOpen: false,
            diacriticInsensitiveFilter,
        };
    },
    computed: {
        parentValues() {
            return asParentArray(this.parent, this.multiple);
        },
        hasParentSelection() {
            return this.parentValues.length > 0;
        },
        hasSelection() {
            return this.hasParentSelection || (this.child != null && this.child !== '');
        },
        displayValue() {
            if (!this.hasParentSelection) {
                return '';
            }
            const parentTitles = this.parentValues.map((value) => {
                const match = (this.parentItems || []).find(
                    (item) => String(item?.[this.itemValue]) === String(value),
                );
                return match?.[this.itemTitle] || String(value);
            });
            const parentText = parentTitles.join(', ');
            if (this.child == null || this.child === '') {
                return `${parentText} › …`;
            }
            const childMatch = (this.childItems || []).find(
                (item) => String(item?.[this.itemValue]) === String(this.child),
            );
            const childTitle = childMatch?.[this.itemTitle] || String(this.child);
            return `${parentText} › ${childTitle}`;
        },
    },
    methods: {
        onParentChange(value) {
            const nextParent = this.multiple
                ? (Array.isArray(value) ? value : [])
                : (value == null || value === '' ? null : value);
            this.$emit('update:parent', nextParent);
            this.$emit('update:child', null);
            this.$emit('parent-change', nextParent);
        },
        onChildChange(value) {
            const nextChild = value == null || value === '' ? null : value;
            this.$emit('update:child', nextChild);
            this.$emit('child-change', nextChild);
            if (this.closeOnChildSelect && nextChild != null) {
                this.menuOpen = false;
            }
        },
        clearSelection() {
            const emptyParent = this.multiple ? [] : null;
            this.$emit('update:parent', emptyParent);
            this.$emit('update:child', null);
            this.$emit('parent-change', emptyParent);
            this.$emit('clear');
            this.menuOpen = false;
        },
    },
};
</script>
